import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Controller } from "react-hook-form";

const ITEM_H = 40;

const WheelColumn = ({ items, value, onChange, disabled }) => {
  var ref = useRef(null);
  var timer = useRef(null);
  var suppress = useRef(false);

  useEffect(function () {
    if (!ref.current) return;
    var idx = items.indexOf(value);
    if (idx < 0) idx = 0;
    suppress.current = true;
    ref.current.scrollTop = idx * ITEM_H;
    setTimeout(function () { suppress.current = false; }, 60);
  }, [value, items]);

  var handleScroll = useCallback(function () {
    if (suppress.current || disabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(function () {
      var el = ref.current;
      if (!el) return;
      var idx = Math.round(el.scrollTop / ITEM_H);
      if (idx < 0) idx = 0;
      if (idx > items.length - 1) idx = items.length - 1;
      el.scrollTo({ top: idx * ITEM_H, behavior: "smooth" });
      if (items[idx] !== value) onChange(items[idx]);
    }, 120);
  }, [items, value, onChange, disabled]);

  return (
    <div ref={ref} onScroll={handleScroll} className="tpf-wheel" style={{ pointerEvents: disabled ? "none" : "auto" }}>
      <div style={{ height: ITEM_H }} />
      {items.map(function (it) {
        return (
          <div
            key={it}
            className={"tpf-wheel-item" + (it === value ? " active" : "")}
            onClick={function () { if (!disabled) onChange(it); }}
          >
            {it}
          </div>
        );
      })}
      <div style={{ height: ITEM_H }} />
    </div>
  );
};

export const TimePickerField = ({
  control,
  name,
  label = "Time",
  placeholder = "Select Time",
  rules = {},
  className = "",
  disabled = false,
  disabledMarginBottom,
  minuteStep = 1,
  allowInput = true,
  ...props
}) => {
  // 12-hour mode only: hour wheel is always 01–12 with an AM/PM selector.
  var use24hr = false;

  var isRequired = !!rules?.required;
  var [open, setOpen] = useState(false);
  var wrapRef = useRef(null);
  var anchorRef = useRef(null);
  var popRef = useRef(null);

  // hours: 01 .. 12 only
  var hours = [];
  for (var h = 1; h <= 12; h++) hours.push(String(h).padStart(2, "0"));

  var minutes = [];
  for (var m = 0; m < 60; m += minuteStep) minutes.push(String(m).padStart(2, "0"));

  function parse(value) {
    var d = new Date();
    var hh = d.getHours();
    var mm = d.getMinutes();
    if (value) {
      if (value instanceof Date) { hh = value.getHours(); mm = value.getMinutes(); }
      else {
        var p = String(value).split(":");
        hh = Number(p[0]) || 0;
        mm = Number(p[1]) || 0;
      }
    }
    var ap = hh >= 12 ? "PM" : "AM";
    var disp = hh % 12 === 0 ? 12 : hh % 12;
    return { h: String(disp).padStart(2, "0"), m: String(mm).padStart(2, "0"), ap: ap };
  }

  function toStore(hv, mv, apv) {
    var hh = Number(hv);
    if (apv === "PM" && hh !== 12) hh += 12;
    if (apv === "AM" && hh === 12) hh = 0;
    return String(hh).padStart(2, "0") + ":" + mv + ":00";
  }

  function display(value) {
    if (!value) return "";
    var p = parse(value);
    return p.h + ":" + p.m + " " + p.ap;
  }

  // Lenient manual-entry parser.
  // Accepts: "9", "930", "9:3", "9 30 pm", "0930", "21:05", "1.30pm", "9:30 a", "9:30 P"
  function parseTyped(raw) {
    if (!raw) return null;
    var s = String(raw).trim().toLowerCase();
    if (!s) return null;

    var meridiem = null;
    if (/p/.test(s)) meridiem = "PM";
    else if (/a/.test(s)) meridiem = "AM";

    var digits = s.replace(/[^0-9]/g, "");
    if (!digits.length) return null;

    var hh, mm;
    if (digits.length <= 2) { hh = Number(digits); mm = 0; }
    else if (digits.length === 3) { hh = Number(digits.slice(0, 1)); mm = Number(digits.slice(1)); }
    else { hh = Number(digits.slice(0, 2)); mm = Number(digits.slice(2, 4)); }

    if (isNaN(hh) || isNaN(mm) || mm > 59) return null;
    if (hh > 23) return null;

    // 24h-style entry -> fold into 1–12 + meridiem
    if (hh > 12) { meridiem = "PM"; hh -= 12; }
    else if (hh === 0) { meridiem = "AM"; hh = 12; }
    if (!meridiem) meridiem = "AM";
    if (hh < 1 || hh > 12) return null;

    // snap minutes to step
    if (minuteStep > 1) mm = Math.round(mm / minuteStep) * minuteStep % 60;

    return {
      h: String(hh).padStart(2, "0"),
      m: String(mm).padStart(2, "0"),
      ap: meridiem,
      hadMeridiem: /a|p/.test(s),
    };
  }

  useEffect(function () {
    function onDoc(e) {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return function () { document.removeEventListener("mousedown", onDoc); };
  }, []);

  return (
    <div className={disabledMarginBottom ? "mb-0" : "mb-3"} ref={wrapRef} style={{ position: "relative" }}>
      <style>{`
        .required-asterisk { color: red; margin-left: 4px; }
        .tpf-pop {
          position: fixed; z-index: 1060;
          width: 300px; padding: 14px;
          background: var(--vz-secondary-bg, #fff);
          border: 1px solid var(--vz-border-color, #e9ebec);
          border-radius: 16px;
          box-shadow: 0 12px 32px rgba(30,40,90,.18);
        }
        .tpf-preview {
          display: flex; align-items: center; gap: 10px;
          border: 2px solid var(--vz-primary, #6559ff);
          border-radius: 12px; padding: 6px 14px; margin-bottom: 12px;
        }
        .tpf-preview input {
          flex: 1; min-width: 0; border: 0; outline: 0; background: transparent;
          font-size: 20px; font-weight: 700; letter-spacing: 1px; padding: 4px 0;
          color: var(--vz-body-color, #212529);
        }
        .tpf-preview-ap {
          font-size: 13px; font-weight: 700; padding: 4px 10px; border-radius: 8px;
          background: rgba(101,89,255,.12); color: var(--vz-primary, #6559ff);
          cursor: pointer; user-select: none;
        }
        .tpf-tabs { display: flex; gap: 8px; margin-bottom: 10px; }
        .tpf-tab {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
          padding: 8px 0; border-radius: 10px; font-size: 13px; font-weight: 600;
          border: 1px solid var(--vz-border-color, #e9ebec); cursor: pointer;
          background: transparent; color: var(--vz-body-color, #212529);
        }
        .tpf-tab.active { background: var(--vz-primary, #6559ff); border-color: var(--vz-primary, #6559ff); color: #fff; }
        .tpf-body { display: flex; gap: 10px; align-items: center; }
        .tpf-wheels { position: relative; flex: 1; display: flex; gap: 4px; height: ${ITEM_H * 3}px; overflow: hidden; }
        .tpf-wheels::before {
          content: ""; position: absolute; left: 0; right: 0; top: ${ITEM_H}px;
          height: ${ITEM_H}px; border-radius: 10px;
          background: rgba(101,89,255,.08);
          border-top: 1px solid rgba(101,89,255,.25);
          border-bottom: 1px solid rgba(101,89,255,.25);
          pointer-events: none;
        }
        .tpf-wheel { flex: 1; height: 100%; overflow-y: auto; scroll-snap-type: y mandatory; scrollbar-width: none; text-align: center; }
        .tpf-wheel::-webkit-scrollbar { display: none; }
        .tpf-wheel-item {
          height: ${ITEM_H}px; line-height: ${ITEM_H}px; scroll-snap-align: center;
          font-size: 16px; font-weight: 600; cursor: pointer; color: var(--vz-secondary-color, #adb5bd);
        }
        .tpf-wheel-item.active { color: var(--vz-primary, #6559ff); font-size: 19px; }
        .tpf-ap { display: flex; flex-direction: column; gap: 6px; }
        .tpf-ap button {
          width: 46px; padding: 8px 0; border-radius: 10px; font-size: 12px; font-weight: 700;
          border: 1px solid var(--vz-border-color, #e9ebec); background: transparent;
          color: var(--vz-body-color, #212529); cursor: pointer;
        }
        .tpf-ap button.active { background: var(--vz-primary, #6559ff); border-color: var(--vz-primary, #6559ff); color: #fff; }
        .tpf-actions { display: flex; gap: 8px; margin-top: 14px; }
        .tpf-actions button { flex: 1; padding: 8px 0; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; }
        .tpf-cancel { background: transparent; border: 1px solid var(--vz-border-color, #e9ebec); color: var(--vz-body-color, #212529); }
        .tpf-done { background: var(--vz-primary, #6559ff); border: 1px solid var(--vz-primary, #6559ff); color: #fff; }
      `}</style>

      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {isRequired && <span className="required-asterisk">*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TimePickerPanel
            field={field}
            fieldState={fieldState}
            parse={parse}
            parseTyped={parseTyped}
            toStore={toStore}
            display={display}
            open={open}
            setOpen={setOpen}
            disabled={disabled}
            allowInput={allowInput}
            placeholder={placeholder}
            className={className}
            name={name}
            hours={hours}
            minutes={minutes}
            use24hr={use24hr}
            anchorRef={anchorRef}
            popRef={popRef}
            {...props}
          />
        )}
      />
    </div>
  );
};

const TimePickerPanel = ({
  field, fieldState, parse, parseTyped, toStore, display,
  open, setOpen, disabled, allowInput, placeholder, className, name,
  hours, minutes, use24hr, anchorRef, popRef, ...props
}) => {
  var init = parse(field.value);

  var [h, setH] = useState(init.h);
  var [m, setM] = useState(init.m);
  var [ap, setAp] = useState(init.ap);

  // outer input buffer
  var [text, setText] = useState(display(field.value));
  var [typing, setTyping] = useState(false);

  // popup preview buffer
  var [popText, setPopText] = useState("");
  var [popTyping, setPopTyping] = useState(false);

  var [coords, setCoords] = useState({ top: 0, left: 0 });

  // single formatter used by BOTH text boxes -> always hh:mm AM/PM
  function fmt(hv, mv, apv) {
    return hv + ":" + mv + " " + apv;
  }

  useLayoutEffect(function () {
    if (!open) return;
    function updateCoords() {
      if (!anchorRef?.current) return;
      var rect = anchorRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 6, left: rect.left });
    }
    updateCoords();
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return function () {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open]);

  // external form value changes (only when idle)
  useEffect(function () {
    if (!typing && !open) setText(display(field.value));
  }, [field.value]); // eslint-disable-line

  // sync wheels when popup opens
  useEffect(function () {
    if (open) {
      var p = parse(field.value);
      setH(p.h); setM(p.m); setAp(p.ap);
      setPopTyping(false);
      setPopText(fmt(p.h, p.m, p.ap));
      setTyping(false);
    }
  }, [open]); // eslint-disable-line

  // REALTIME: wheels + AM/PM -> popup preview
  useEffect(function () {
    if (!popTyping) setPopText(fmt(h, m, ap));
  }, [h, m, ap]); // eslint-disable-line

  // REALTIME: wheels + AM/PM -> outer input
  useEffect(function () {
    if (!open || typing) return;
    setText(fmt(h, m, ap));
  }, [h, m, ap, open]); // eslint-disable-line

  // Typing in OUTER input -> drives wheels, AM/PM and popup preview live
  function onOuterChange(e) {
    if (!allowInput) return;
    var val = e.target.value;
    setTyping(true);
    setText(val);

    var p = parseTyped(val);
    if (p) {
      var apv = p.hadMeridiem ? p.ap : ap;
      setPopTyping(false);
      setH(p.h);
      setM(p.m);
      setAp(apv);
      setPopText(fmt(p.h, p.m, apv));
    }
  }

  // Typing in POPUP preview -> drives wheels, AM/PM and outer input live
  function onPopChange(e) {
    if (!allowInput) return;
    var val = e.target.value;
    setPopTyping(true);
    setPopText(val);

    var p = parseTyped(val);
    if (p) {
      var apv = p.hadMeridiem ? p.ap : ap;
      setH(p.h);
      setM(p.m);
      setAp(apv);
      setTyping(false);
      setText(fmt(p.h, p.m, apv));
    }
  }

  // Outer input commit -> writes to form value
  function commitText() {
    setTyping(false);
    var raw = text.trim();
    if (!raw) { field.onChange(null); setText(""); return; }

    var p = parseTyped(raw);
    if (!p) { setText(display(field.value)); return; } // revert bad input

    var meridiem = p.hadMeridiem ? p.ap : ap;
    var stored = toStore(p.h, p.m, meridiem);
    field.onChange(stored);
    setH(p.h); setM(p.m); setAp(meridiem);
    setPopText(fmt(p.h, p.m, meridiem));
    setText(display(stored));
  }

  // Popup preview commit -> normalize buffer only (Done writes the form value)
  function commitPopText() {
    setPopTyping(false);
    var p = parseTyped(popText);
    if (!p) { setPopText(fmt(h, m, ap)); return; }
    var apv = p.hadMeridiem ? p.ap : ap;
    setH(p.h);
    setM(p.m);
    setAp(apv);
    setPopText(fmt(p.h, p.m, apv));
  }

  function commit() {
    var hv = h, mv = m, apv = ap;
    if (popTyping) {
      var p = parseTyped(popText);
      if (p) {
        hv = p.h; mv = p.m;
        if (p.hadMeridiem) apv = p.ap;
      }
    }
    var stored = toStore(hv, mv, apv);
    field.onChange(stored);
    setH(hv); setM(mv); setAp(apv);
    setText(display(stored));
    setPopText(fmt(hv, mv, apv));
    setPopTyping(false);
    setTyping(false);
    setOpen(false);
  }

  function cancel() {
    var p = parse(field.value);
    setH(p.h); setM(p.m); setAp(p.ap);
    setPopText(fmt(p.h, p.m, p.ap));
    setPopTyping(false);
    setTyping(false);
    setText(display(field.value));
    setOpen(false);
  }

  function now() {
    var d = new Date();
    var hh = d.getHours();
    var nap = hh >= 12 ? "PM" : "AM";
    var disp = hh % 12 === 0 ? 12 : hh % 12;
    setPopTyping(false);
    setTyping(false);
    setH(String(disp).padStart(2, "0"));
    setM(String(d.getMinutes()).padStart(2, "0"));
    setAp(nap);
  }

  // toggle AM/PM from the pill in the preview
  function toggleAp() {
    setPopTyping(false);
    setTyping(false);
    setAp(ap === "AM" ? "PM" : "AM");
  }

  var popValue = popTyping ? popText : fmt(h, m, ap);

  return (
    <>
      <div className="form-icon" ref={anchorRef}>
        <input
          id={name}
          type="text"
          autoComplete="off"
          readOnly={!allowInput}
          disabled={disabled}
          value={text}
          placeholder={placeholder}
          onChange={onOuterChange}
          onBlur={function () {
            if (allowInput && !open) commitText();
            field.onBlur();
          }}
          onKeyDown={function (e) {
            if (e.key === "Enter") { e.preventDefault(); commitText(); setOpen(false); }
            if (e.key === "Escape") { cancel(); }
            if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); }
            // quick meridiem toggle from the outer box
            if (e.key === "a" || e.key === "A") { e.preventDefault(); setTyping(false); setAp("AM"); }
            if (e.key === "p" || e.key === "P") { e.preventDefault(); setTyping(false); setAp("PM"); }
          }}
          onFocus={function () { if (!disabled) setOpen(true); }}
          className={
            "form-control form-control-icon custom-input " +
            (fieldState.error ? "is-invalid " : "") + className
          }
          style={{ cursor: disabled ? "not-allowed" : "text" }}
          {...props}
        />
        <div style={{ top: "17px", position: "absolute" }}>
          <i className="ri-time-line text-muted"></i>
        </div>
        {fieldState.error && <div className="invalid-feedback">{fieldState.error.message}</div>}
      </div>

      {open && !disabled && createPortal(
        <div className="tpf-pop" ref={popRef} style={{ top: coords.top, left: coords.left }}>
          <div className="text-muted mb-2" style={{ fontSize: 12, fontWeight: 600 }}>Select Time</div>

          <div className="tpf-preview">
            <i className="ri-time-line text-muted"></i>
            <input
              type="text"
              value={popValue}
              readOnly={!allowInput}
              onChange={onPopChange}
              onFocus={function () {
                if (!allowInput) return;
                setPopTyping(true);
                setPopText(fmt(h, m, ap));
              }}
              onBlur={commitPopText}
              onKeyDown={function (e) {
                if (e.key === "Enter") { e.preventDefault(); commitPopText(); }
                if (e.key === "Escape") { cancel(); }
              }}
              aria-label="Type time"
            />
            <span className="tpf-preview-ap" onClick={toggleAp} title="Toggle AM / PM">
              {ap}
            </span>
          </div>

          <div className="tpf-tabs">
            <button type="button" className="tpf-tab active">
              <i className="ri-time-line"></i> Time
            </button>
            <button type="button" className="tpf-tab" onClick={now}>
              <i className="ri-sun-line"></i> Now
            </button>
          </div>

          <div className="tpf-body">
            <div className="tpf-wheels">
              <WheelColumn
                items={hours}
                value={h}
                onChange={function (v) { setPopTyping(false); setTyping(false); setH(v); }}
              />
              <WheelColumn
                items={minutes}
                value={m}
                onChange={function (v) { setPopTyping(false); setTyping(false); setM(v); }}
              />
            </div>

            <div className="tpf-ap">
              <button
                type="button"
                className={ap === "AM" ? "active" : ""}
                onClick={function () { setPopTyping(false); setTyping(false); setAp("AM"); }}
              >AM</button>
              <button
                type="button"
                className={ap === "PM" ? "active" : ""}
                onClick={function () { setPopTyping(false); setTyping(false); setAp("PM"); }}
              >PM</button>
            </div>
          </div>

          <div className="tpf-actions">
            <button type="button" className="tpf-cancel" onClick={cancel}>Cancel</button>
            <button type="button" className="tpf-done" onClick={commit}>Done</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TimePickerField;